// University of Utah: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - The U's Common Data Set 2025-2026 (CDS_2025-2026.pdf, posted by
//    University Analytics and Institutional Reporting on uair.utah.edu; the
//    old data.utah.edu Common Data Set page now returns 404 and UAIR's
//    reporting page is password-protected, so the SOURCES url is the PDF on
//    the utah.edu host). C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21/C22 early plans;
//    G1/G5 2026-2027 costs. The checkboxes read correctly only in the
//    rendered PDF, not in extracted text. The C1 men, women and unknown
//    rows sum to the residency table's totals (30,036 applied, 27,076
//    admitted, 6,513 enrolled).
//  - admissions.utah.edu: Freshman Students (application deadlines),
//    Freshmen Admissions Standards (Test Optional FAQs) and FAQs (early
//    action decision date, majors that ask for more). The deadline list
//    carries no year; it is the page as posted on 2026-10-01, with the
//    application open.
//  - College Scorecard, school id 230764 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 2026-2027 items are used. The financial aid office's
// 2026-2027 cost of attendance page gives different on-campus totals
// ($45,878 resident, $68,330 non-resident): one tuition and fees line
// ($10,480 and $32,932) and housing and food of $11,300 and $7,898, which it
// calls the average for all undergraduates, adding that freshman costs may
// be $2,000 to $3,000 less. Books, transportation and the $12,000 personal
// line match the CDS.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Utah Common Data Set 2025-2026',
        url: 'https://uair.utah.edu/wp-content/uploads/2026/08/CDS_2025-2026.pdf',
        read: '2026-10-01',
    },
    freshman: {
        label: 'Utah Admissions, Freshman Students',
        url: 'https://admissions.utah.edu/apply/freshman-students/',
        read: '2026-10-01',
    },
    standards: {
        label: 'Utah Admissions, Freshmen Admissions Standards',
        url: 'https://admissions.utah.edu/apply/freshman-students/undergraduate-admissions-standards/',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Utah Admissions, FAQs',
        url: 'https://admissions.utah.edu/information-resources/faqs/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?230764-University-of-Utah',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-utah',
    kbId: 'university_of_utah',
    name: 'University of Utah',
    shortName: 'Utah',
    aliases: ['U of U'],
    location: 'Salt Lake City, UT',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (University of Utah brand colors); decoration only.
    branding: {
        primary: '#BE0000',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_utah_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 30036,
        admitted: 27076,
        enrolled: 6513,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10063, admitted: 9028 },
            { group: 'Out-of-state', applied: 18977, admitted: 17153 },
            { group: 'International', applied: 996, admitted: 895 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'The U\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'The U has no early decision plan. Its early action plan is not restrictive, and the Common Data Set does not report how many applied or were admitted in it, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to the U, not to a major. Some majors ask for more later:',
        rows: [
            { school: 'Majors with their own entry steps', requirement: 'Most majors are open to students once admitted, but some ask for a separate application, an audition or other prerequisites before you can declare. Until then you apply as a pre-major.' },
        ],
        source: 'faqs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Fall application deadline', value: 'April 1' },
        rows: [
            { plan: 'Early Action and merit scholarship consideration', deadline: 'December 1', decision: 'January 15', binding: false },
            { plan: 'Fall deadline', deadline: 'April 1', decision: null, binding: false },
        ],
        note: 'For early action, the application and every required document must be in by December 1; complete files get a decision on January 15. Early action is not binding: you have until May 1 to commit. The U does not publish a decision date for the April 1 deadline.',
        source: 'faqs',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'The U is test-optional for freshman applicants: the SAT or ACT is not required for admission, merit scholarships or admission to a major. If you want scores considered, say so on the application and self-report them; admitted students may be asked for official scores later. You are not penalized for leaving scores out. Scores are required from home school graduates, GED or HiSET recipients and graduates of a U.S. high school that is not regionally accredited.',
        source: 'standards',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by April 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10080, 35282] },
            { item: 'Required fees', amounts: [1086, 1086] },
            { item: 'Housing (on campus)', amounts: [8395, 8395] },
            { item: 'Food (on-campus meal plan)', amounts: [7142, 7142] },
            { item: 'Books and supplies', amounts: [1600, 1600] },
            { item: 'Transportation', amounts: [2600, 2600] },
            { item: 'Other expenses', amounts: [12000, 12000] },
        ],
        totals: [42903, 68105],
        note: 'For a first-year, full-time undergraduate living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 27314,
            byIncome: [
                { income: '$0 to $30,000', amount: 24961 },
                { income: '$30,001 to $48,000', amount: 23936 },
                { income: '$48,001 to $75,000', amount: 25277 },
                { income: '$75,001 to $110,000', amount: 27795 },
                { income: 'Over $110,000', amount: 32141 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from the U\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
